import { useEffect, useState } from "react";
import OrthoAlerts from "@/components/ortho/OrthoAlerts";
import { Link } from "react-router-dom";
import { api, formatMoney, STATUS_LABEL } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { CalendarDays, Users, Wallet, AlertTriangle, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Kpi({ label, value, hint, icon: Icon, accent }) {
  return (
    <div className="card-flat p-5 sm:p-6 flex flex-col gap-3 min-h-[140px]">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent || "bg-muted text-foreground"}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-mono font-bold text-3xl sm:text-4xl tracking-tight">{value}</div>
      {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    pendiente: "bg-accent/15 text-accent",
    confirmada: "bg-primary/15 text-primary",
    en_atencion: "bg-amber-500/20 text-amber-700 dark:text-amber-400",
    finalizada: "bg-muted text-muted-foreground",
    cancelada: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${map[s] || "bg-muted"}`}>
      {STATUS_LABEL[s] || s}
    </span>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[0,1,2,3].map(i => <div key={i} className="card-flat h-[140px] animate-pulse" />)}
      </div>
    );
  }

  const { today_appointments, by_status, cash_summary, total_patients, week_income, session_open } = data;
  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Resumen · {today}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            Hola, {user?.name?.split(" ")[0] || "doctor"}.
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-3">
            Estos son los movimientos clave del consultorio hoy.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/agenda" data-testid="dash-go-agenda" className="btn-primary">
            <CalendarDays className="w-4 h-4" /> Ver agenda
          </Link>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Citas hoy"
          value={(today_appointments || []).length}
          hint={`${by_status.confirmada || 0} confirmadas · ${by_status.pendiente || 0} pendientes`}
          icon={CalendarDays}
          accent="bg-primary/10 text-primary"
        />
        <Kpi
          label="Ingresos hoy"
          value={formatMoney(cash_summary.income)}
          hint={session_open ? "Caja abierta" : "Caja cerrada / sin abrir"}
          icon={Wallet}
          accent="bg-accent/15 text-accent"
        />
        <Kpi
          label="Pacientes atendidos"
          value={by_status.finalizada || 0}
          hint={`${by_status.en_atencion || 0} en atención`}
          icon={Users}
          accent="bg-muted text-foreground"
        />
        <Kpi
          label="Total pacientes"
          value={total_patients}
          hint="Base de datos clínica"
          icon={Users}
          accent="bg-muted text-foreground"
        />
      </section>

      {(!session_open || (by_status.pendiente || 0) > 0) && (
        <section className="card-flat p-4 sm:p-5 border-l-4 border-l-accent flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-accent mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Atención requerida</div>
            <div className="text-sm text-muted-foreground">
              {!session_open && <>La caja del día aún no está abierta. <Link to="/caja" className="text-primary font-semibold">Abrir caja →</Link></>}
              {(by_status.pendiente || 0) > 0 && (
                <> {!session_open && " · "}Tienes {by_status.pendiente} cita(s) pendientes por confirmar.</>
              )}
            </div>
          </div>
        </section>
      )}

      <OrthoAlerts />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-flat p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Ingresos · últimos 7 días</div>
              <h3 className="font-display font-bold text-xl tracking-tight mt-1">Tendencia semanal</h3>
            </div>
            <Link to="/reportes" className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline">
              Reportes <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={week_income} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => formatMoney(v)}
                />
                <Area type="monotone" dataKey="ingreso" stroke="hsl(var(--primary))" fill="url(#incomeFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Agenda</div>
              <h3 className="font-display font-bold text-xl tracking-tight mt-1">Citas de hoy</h3>
            </div>
            <span className="text-xs font-mono text-muted-foreground">{(today_appointments || []).length} total</span>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-auto pr-1">
            {(today_appointments || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">Sin citas programadas hoy.</div>
            )}
            {(today_appointments || []).map((a) => (
              <div key={a.id} className={`p-3 rounded-sm bg-card border border-border stripe-${a.status} flex items-start gap-3`}>
                <div className="font-mono text-xs text-muted-foreground w-12 shrink-0">
                  {new Date(a.start).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.patient?.name || "Paciente"}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.treatment_name}</div>
                </div>
                <StatusPill s={a.status} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
